import React from 'react'
import { Box, Button, SvgIcon, Typography } from '@mui/material'

import { useAppStore } from '@/contexts/store'

import { WPCF7Form } from '@/types'

export type FileUploadFieldProps = {
  field: WPCF7Form['form'][0]
  formik: any
}

const FileUploadField = ({ field, formik }: FileUploadFieldProps) => {
  const { strings } = useAppStore()
  const formatMaxFileSize = (maxFileSize?: string) => {
    if (!maxFileSize) return null

    const normalizedValue = String(maxFileSize).trim()
    if (!normalizedValue) return null

    if (/^[\d.]+$/.test(normalizedValue)) {
      return `${normalizedValue} MB`
    }

    return normalizedValue.toUpperCase().replace(/\s+/g, ' ')
  }

  const formattedMaxFileSize = formatMaxFileSize(field.maxFileSize)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      formik.setFieldValue(`input_${field.id}`, files[0])
    } else {
      formik.setFieldValue(`input_${field.id}`, null)
    }
  }

  const selectedFile = formik.values[`input_${field.id}`]

  return (
    <>
      {field.label && field.labelPlacement !== 'hidden_label' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1,
            ...(formik.submitCount > 0 &&
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined && {
                color: 'error.main'
              })
          }}
        >
          {field.label}
          {field.isRequired ? ' *' : ''}
        </Typography>
      )}
      <Box
        sx={{
          border: '2px dashed',
          borderColor:
            formik.submitCount > 0 &&
            formik.errors[`input_${field.id}`] &&
            formik.touched[`input_${field.id}`]
              ? 'error.main'
              : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: 'primary.main'
          },
          ...(formik.isSubmitting && {
            pointerEvents: 'none',
            opacity: 0.4
          })
        }}
        onClick={() => document.getElementById(`file-input-${field.id}`)?.click()}
      >
        <input
          id={`file-input-${field.id}`}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="*/*"
          multiple={field.multipleFiles || false}
          disabled={formik.isSubmitting}
        />
        <SvgIcon fontSize="medium">
          <use href="#ico-upload" />
        </SvgIcon>
        <Typography variant="textSmall" color="text.secondary">
          {selectedFile ? selectedFile.name : strings?.labels.select_file}
        </Typography>
        {selectedFile && (
          <Typography variant="caption" color="text.secondary">
            {strings?.labels.size}: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
        )}
        {field.placeholder && !selectedFile && (
          <Typography variant="caption" color="text.secondary">
            {field.placeholder}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'space-between' }}>
        <Box>
          {field.description && (
            <Typography variant="textSmall" color="text.secondary">
              {field.description}
            </Typography>
          )}
          {formattedMaxFileSize && (
            <Typography variant="textSmall" color="text.secondary" sx={{ display: 'block' }}>
              Max file size: {formattedMaxFileSize}
            </Typography>
          )}
        </Box>
        {selectedFile && (
          <Box>
            <Button
              size="small"
              variant="text"
              sx={{ typography: 'caption', p: '0px !important' }}
              onClick={() => formik.setFieldValue(`input_${field.id}`, null)}
              disabled={formik.isSubmitting}
            >
              {strings?.labels.remove}
            </Button>
          </Box>
        )}
      </Box>
      {formik.submitCount > 0 &&
        formik.touched[`input_${field.id}`] &&
        formik.errors[`input_${field.id}`] && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {String(formik.errors[`input_${field.id}`])}
          </Typography>
        )}
    </>
  )
}

export default FileUploadField
